import { request } from '@/api/apiUtils';
import { createEntityQuery } from '@/data/createEntityQuery';
import { useTextAtomTypesDictionary } from '@/data/entities/textAtomTypes';
import type { HostedNoteParent } from '@/data/hostedNoteAtoms';
import {
    ACT_NOTE_ATOM_CODE,
    FRAGMENT_NOTE_ATOM_CODE,
    fromSceneNoteAtom,
    SCENE_NOTE_ATOM_CODE,
    type SceneNoteAtomLike,
} from '@/data/sceneNoteTextAtoms';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import type { NoteRecord } from '@/utils/fountain/noteMarker';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010).
 *
 *  Notes get a root of their own even though their atoms also ride along inside
 *  the scene payload: it is what lets a note write invalidate notes and nothing
 *  else. Keyed to the scene list instead, every body keystroke inside a card
 *  would refetch (and re-sync) the whole screenplay. */
export const SCENE_NOTES_ROOT = ['scene-notes'] as const;

/** A note as it comes back from the API: the record the engine joins to a
 *  marker, plus the transport state that record deliberately has no room for
 *  (see `sceneNoteTextAtoms` — a note's identity is its uuid all the way down,
 *  the integer id only addresses `PUT`/`DELETE .../text-atoms/:id`). */
export interface SceneNote {
    /** Server id of the backing text atom — what later writes address. */
    atomId: number;
    /** The scene the atom hangs off (`textable_id`). */
    sceneId: number;
    record: NoteRecord;
}

interface SceneNotesListParams {
    projectId: number;
    /** Resolved by `useSceneNoteAtomTypeId`; `undefined` until the dictionary
     *  answers, which is a "don't read yet", not "read everything". */
    typeId: number | undefined;
    version?: string;
}

interface TextAtomIndexPage {
    data: (SceneNoteAtomLike & { id?: number; textable_id?: number })[];
    meta?: { current_page?: number; last_page?: number };
}

/** Notes per request. The server takes any `per_page` and caps a scene at 100
 *  notes, so this is ~5 fully-annotated scenes per round trip — one request for
 *  all but the largest projects, without asking for an unbounded page. */
const PER_PAGE = 500;

/** Stop walking after this many pages (250k notes). A backstop against a
 *  server-side paginator that never advances `current_page`, not a real limit. */
const MAX_PAGES = 500;

const fetchers = {
    /**
     * Every note in the project, in one list.
     *
     * Project-wide rather than per-scene because that is the span a marker can
     * travel: a note cut from one scene and pasted into another keeps its uuid,
     * and the join is by uuid alone. Scoping the read to the open episode would
     * turn each such note into an orphan for as long as the reader stayed on
     * that episode.
     */
    list: {
        key: ({ projectId, typeId, version }: SceneNotesListParams) =>
            [...SCENE_NOTES_ROOT, version !== undefined ? { projectId, typeId, version } : { projectId, typeId }] as const,
        fetch: async ({ projectId, typeId, version }: SceneNotesListParams): Promise<SceneNote[]> => {
            // No resolved type id means no filter, and no filter means reading
            // every text atom in the project — the body of every scene, every
            // synopsis, every logline — to find the notes. Read nothing instead.
            if (typeId === undefined) return [];

            const notes: SceneNote[] = [];
            for (let page = 1; page <= MAX_PAGES; page++) {
                const params = new URLSearchParams({
                    text_atom_type_id: String(typeId),
                    per_page: String(PER_PAGE),
                    page: String(page),
                });
                if (version) params.append('version', version);
                const response: TextAtomIndexPage = await request(`projects/${projectId}/text-atoms?${params}`);

                for (const atom of response.data ?? []) {
                    const record = fromSceneNoteAtom(atom);
                    // An atom with no uuid can never be joined to a marker;
                    // `fromSceneNoteAtom` says so by returning null, and reading
                    // the rest of the project's notes matters more than it does.
                    if (record === null || atom.id === undefined) continue;
                    notes.push({ atomId: atom.id, sceneId: atom.textable_id ?? 0, record });
                }

                const { current_page: current = page, last_page: last = page } = response.meta ?? {};
                if (current >= last) break;
            }
            return notes;
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const sceneNotesEntity: EntityDefinition<typeof fetchers> = {
    name: 'sceneNotes',
    roots: [SCENE_NOTES_ROOT],
    fetchers,
};

export const useSceneNotesList = createEntityQuery(sceneNotesEntity, 'list');

/**
 * The numeric `text_atom_type_id` of `scene_note` in THIS deployment.
 *
 * Every other member of the `textAtomTypeId` enum is a hard-coded literal, and
 * `scene_note` deliberately is not: the migration that adds it
 * (`2026_07_30_113512_add_scene_note_text_atom_type`) inserts a row and lets
 * the sequence assign the id, so the number differs per environment and cannot
 * be known before the deploy. Reads filter by it and writes carry it (`POST
 * text-atoms` takes the id, not the code), so it is resolved at runtime from
 * the dictionary and pinned for the session — that table only changes when a
 * migration runs.
 *
 * `undefined` while the dictionary is in flight, and on a deployment where the
 * migration has not run. Both mean the same thing to callers: notes are not
 * available yet, so don't read and don't write.
 */
export function useSceneNoteAtomTypeId(): number | undefined {
    return useNoteAtomTypeId(SCENE_NOTE_ATOM_CODE);
}

/**
 * The same answer for the act / fragment note types (#1840-18).
 *
 * Only WRITES need it: a hosted note is read off the act / fragment payload by
 * `type.code` (`data/hostedNoteAtoms.ts`), so an unresolved dictionary cannot
 * hide a note that exists — it only means a new one cannot be created yet,
 * which is the same «notes are read-only for now» the scene half says.
 */
export function useHostedNoteAtomTypeId(parent: HostedNoteParent): number | undefined {
    return useNoteAtomTypeId(parent === 'act' ? ACT_NOTE_ATOM_CODE : FRAGMENT_NOTE_ATOM_CODE);
}

/** The dictionary lookup all three share. Pinned for the session — that table
 *  only changes when a migration runs. */
function useNoteAtomTypeId(code: string): number | undefined {
    const { data } = useTextAtomTypesDictionary(undefined, {
        overrides: { staleTime: Number.POSITIVE_INFINITY, gcTime: Number.POSITIVE_INFINITY },
    });
    return data?.find(type => type.code === code)?.id;
}

export type { SceneNotesListParams };
